package com.evoapps.evoshift.ui.kiosk

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.NoteAdd
import androidx.compose.material.icons.filled.CloudOff
import androidx.compose.material.icons.filled.EditNote
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.Checklists
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.domain.repository.KioskChecklistMoment
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.checklists.ChecklistHeader
import com.evoapps.evoshift.ui.checklists.ChecklistItemRow
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.ChecklistNoteDraft
import com.evoapps.evoshift.viewmodels.KioskChecklistState
import com.evoapps.evoshift.viewmodels.KioskViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_add_note
import evoshift.composeapp.generated.resources.checklist_edit_note
import evoshift.composeapp.generated.resources.checklist_note
import evoshift.composeapp.generated.resources.checklist_note_required
import evoshift.composeapp.generated.resources.done
import evoshift.composeapp.generated.resources.kiosk_checklist_hint_close
import evoshift.composeapp.generated.resources.kiosk_checklist_hint_open
import evoshift.composeapp.generated.resources.kiosk_checklist_needs_connection
import evoshift.composeapp.generated.resources.kiosk_checklist_skip
import evoshift.composeapp.generated.resources.kiosk_checklist_skip_close
import evoshift.composeapp.generated.resources.kiosk_checklist_skip_note
import evoshift.composeapp.generated.resources.kiosk_checklist_title_close
import evoshift.composeapp.generated.resources.kiosk_checklist_title_open
import evoshift.composeapp.generated.resources.kiosk_clock_out
import evoshift.composeapp.generated.resources.save
import org.jetbrains.compose.resources.stringResource

/** Test tags of the tablet's checklist (SH-127). */
object KioskChecklistTags {
    const val PANEL = "kiosk_checklist"
    const val FINISH = "kiosk_checklist_finish"
    const val SKIP = "kiosk_checklist_skip"
    const val OFFLINE = "kiosk_checklist_offline"
    const val NOTE_FIELD = "kiosk_checklist_note_field"
    fun noteButton(checklistId: String, itemId: String) = "kiosk_checklist_note_${checklistId}_$itemId"
}

/**
 * SH-127: the shift's checklists over the tablet's PIN pad (before a clock-out: "End shift" when
 * everything is ticked, else "Skip and end shift") or over the confirmation (after a clock-in, the
 * opening ones: "Done" or "Skip"). Large rows, notes in a dialog; without a connection it says
 * that ticks need one.
 */
@Composable
fun KioskChecklistOverlay(viewModel: KioskViewModel, state: KioskChecklistState, isSending: Boolean) {
    val closing = state.moment == KioskChecklistMoment.CLOSE
    Box(
        Modifier
            .fillMaxSize()
            .background(DarkMetallicBackgroundBrush)
            // Touches here never reach the PIN pad or the confirmation underneath.
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = {})
            .testTag(KioskChecklistTags.PANEL),
        contentAlignment = Alignment.TopCenter
    ) {
        LazyColumn(
            modifier = Modifier.fillMaxSize().widthIn(max = 760.dp),
            contentPadding = PaddingValues(horizontal = 24.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            item("title") {
                Column {
                    Text(
                        stringResource(if (closing) Res.string.kiosk_checklist_title_close else Res.string.kiosk_checklist_title_open),
                        color = Color.White,
                        fontSize = 30.sp,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        stringResource(
                            if (closing) Res.string.kiosk_checklist_hint_close else Res.string.kiosk_checklist_hint_open,
                            state.person.displayName
                        ),
                        color = Color.White.copy(alpha = 0.8f),
                        style = MaterialTheme.typography.titleMedium
                    )
                }
            }
            if (state.offline) {
                item("offline") {
                    Row(
                        Modifier.fillMaxWidth().clip(RoundedCornerShape(12.dp)).background(Color(0xFF8A5A00)).padding(12.dp)
                            .testTag(KioskChecklistTags.OFFLINE),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.CloudOff, contentDescription = null, tint = Color.White, modifier = Modifier.size(20.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.kiosk_checklist_needs_connection), color = Color.White)
                    }
                }
            }
            state.error?.takeIf { !state.offline }?.let { error ->
                item("error") {
                    Text(error.asString(), color = Color(0xFFFF8A80), style = MaterialTheme.typography.titleMedium)
                }
            }
            items(state.checklists, key = { it.id }) { checklist ->
                KioskChecklistCard(
                    checklist = checklist,
                    onToggle = { item -> viewModel.toggleChecklistItem(checklist.id, item) },
                    onNote = { item -> viewModel.editChecklistNote(checklist.id, item) }
                )
            }
            item("actions") {
                Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth().padding(top = 8.dp)) {
                    GoldButton(
                        text = stringResource(if (closing) Res.string.kiosk_clock_out else Res.string.done),
                        onClick = viewModel::finishChecklist,
                        enabled = state.isComplete && !state.isClosing,
                        isBusy = state.isClosing || isSending,
                        modifier = Modifier.fillMaxWidth().widthIn(max = 420.dp).heightIn(min = 72.dp).testTag(KioskChecklistTags.FINISH)
                    )
                    Spacer(Modifier.size(12.dp))
                    OutlinedButton(
                        onClick = viewModel::skipChecklist,
                        enabled = !state.isComplete && !state.isClosing,
                        border = BorderStroke(2.dp, LightGold),
                        modifier = Modifier.fillMaxWidth().widthIn(max = 420.dp).heightIn(min = 64.dp).handCursor().testTag(KioskChecklistTags.SKIP)
                    ) {
                        Text(
                            stringResource(if (closing) Res.string.kiosk_checklist_skip_close else Res.string.kiosk_checklist_skip),
                            color = LightGold,
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Spacer(Modifier.size(8.dp))
                    Text(
                        stringResource(Res.string.kiosk_checklist_skip_note),
                        color = Color.White.copy(alpha = 0.6f),
                        style = MaterialTheme.typography.bodySmall,
                        textAlign = TextAlign.Center
                    )
                }
            }
        }
    }

    state.noteDraft?.let { draft ->
        KioskNoteDialog(
            draft,
            onChange = viewModel::updateChecklistNote,
            onSave = viewModel::saveChecklistNote,
            onDismiss = viewModel::dismissChecklistNote
        )
    }
}

@Composable
private fun KioskChecklistCard(
    checklist: ShiftChecklist,
    onToggle: (com.evoapps.evoshift.domain.model.ChecklistItem) -> Unit,
    onNote: (com.evoapps.evoshift.domain.model.ChecklistItem) -> Unit
) {
    RegularCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            ChecklistHeader(checklist, Modifier.padding(bottom = 4.dp))
            checklist.items.forEach { item ->
                ChecklistItemRow(
                    checklist = checklist,
                    item = item,
                    onToggle = { onToggle(item) },
                    trailing = {
                        val hasNote = checklist.noteOf(item.id) != null
                        IconButtonWithTooltip(
                            onClick = { onNote(item) },
                            modifier = Modifier.size(56.dp).handCursor().testTag(KioskChecklistTags.noteButton(checklist.id, item.id)),
                            tooltip = stringResource(if (hasNote) Res.string.checklist_edit_note else Res.string.checklist_add_note)
                        ) {
                            Icon(
                                if (hasNote) Icons.Default.EditNote else Icons.AutoMirrored.Filled.NoteAdd,
                                contentDescription = stringResource(if (hasNote) Res.string.checklist_edit_note else Res.string.checklist_add_note),
                                tint = DeepNavy.copy(alpha = 0.7f)
                            )
                        }
                    }
                )
            }
        }
    }
}

/** A task's note (what was counted or measured); saving it ticks the task. */
@Composable
private fun KioskNoteDialog(draft: ChecklistNoteDraft, onChange: (String) -> Unit, onSave: () -> Unit, onDismiss: () -> Unit) {
    EvoDialog(
        title = draft.item.text,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(stringResource(Res.string.save), onSave, enabled = !draft.item.requiresNote || draft.note.isNotBlank())
    ) {
        EvoTextField(
            value = draft.note,
            onValueChange = { onChange(it.take(Checklists.NOTE_MAX_LENGTH)) },
            label = stringResource(Res.string.checklist_note),
            supportingText = if (draft.item.requiresNote) stringResource(Res.string.checklist_note_required) else null,
            singleLine = false,
            minLines = 2,
            modifier = Modifier.testTag(KioskChecklistTags.NOTE_FIELD)
        )
    }
}
